import React, { useState, useEffect } from 'react';
import { AiOutlinePlus } from 'react-icons/ai';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Select } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { request } from '../../../api/request.tsx';

interface RoomStyle {
  _id?: string;
  title: string;
  bannerUrl: File[] | string[];
  bannerMobileUrl: File[] | string[];
}

interface LandingPageData {
  id?: string;
  lang: string;
  isActive: boolean;
  bannerUrl: File[] | string[];
  bannerMobileUrl: File[] | string[];
  introMetadata?: {
    branchMetadata?: {
      bannerUrl: string[];
    };
    bannerUrl: string[];
    bannerMobileUrl: string[];
    _id?: string;
  };
  branchMetadata?: {
    stats?: {
      countBranch: number;
      countFrame: number;
      countUser: number;
      countMember: number;
    };
    brand?: {
      bannerMobileUrl: string[];
      bannerUrl: string[];
      title: string;
      description: string;
    };
    _id?: string;
  };
  roomStyles?: RoomStyle[];
  appMetadata?: {
    goodStation?: {
      bannerMobileUrl: string[];
      bannerUrl: string[];
      title: string;
      description: string;
    };
    bannerMobileUrl: string[];
    bannerUrl: string[];
    _id?: string;
  };
  contactMetadata?: {
    franchise?: {
      bannerUrl: string[];
      bannerMobileUrl: string[];
      title: string;
    };
    contact?: {
      bannerUrl: string[];
      bannerMobileUrl: string[];
      title: string;
    };
    _id?: string;
  };
}

const CreateContentLandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state as LandingPageData | null;
  const isEditMode = !!editData;

  const [formData, setFormData] = useState({
    lang: 'vi',
    isActive: true,
    // Main banners
    bannerUrl: [] as File[] | string[],
    bannerMobileUrl: [] as File[] | string[],
    
    // Intro section
    introBannerUrl: [] as File[] | string[],
    introBannerMobileUrl: [] as File[] | string[],
    introBranchBannerUrl: [] as File[] | string[],
    
    // Branch metadata
    branchBrandTitle: '',
    branchBrandDescription: '',
    branchBrandBannerUrl: [] as File[] | string[],
    
    // Stats
    countBranch: 0,
    countFrame: 0,
    countUser: 0,
    countMember: 0,
    
    // App metadata
    appBannerUrl: [] as File[] | string[],
    goodStationTitle: '',
    goodStationDescription: '',
    goodStationBannerUrl: [] as File[] | string[],
    
    // Contact metadata
    franchiseTitle: '',
    franchiseBannerUrl: [] as File[] | string[],
    contactTitle: 'Liên hệ chúng tôi',
    contactBannerUrl: [] as File[] | string[]
  });

  const [roomStyles, setRoomStyles] = useState<RoomStyle[]>([
    { title: '', bannerUrl: [], bannerMobileUrl: [] }
  ]);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    if (editData) {
      console.log('Loading edit data:', editData);
      
      setFormData({
        lang: editData.lang || 'vi',
        isActive: editData.isActive || true,
        bannerUrl: editData.bannerUrl || [],
        bannerMobileUrl: editData.bannerMobileUrl || [],
        introBannerUrl: editData.introMetadata?.bannerUrl || [],
        introBannerMobileUrl: editData.introMetadata?.bannerMobileUrl || [],
        introBranchBannerUrl: editData.introMetadata?.branchMetadata?.bannerUrl || [],
        branchBrandTitle: editData.branchMetadata?.brand?.title || '',
        branchBrandDescription: editData.branchMetadata?.brand?.description || '',
        branchBrandBannerUrl: editData.branchMetadata?.brand?.bannerUrl || [],
        countBranch: editData.branchMetadata?.stats?.countBranch || 0,
        countFrame: editData.branchMetadata?.stats?.countFrame || 0,
        countUser: editData.branchMetadata?.stats?.countUser || 0,
        countMember: editData.branchMetadata?.stats?.countMember || 0,
        appBannerUrl: editData.appMetadata?.bannerUrl || [],
        goodStationTitle: editData.appMetadata?.goodStation?.title || '',
        goodStationDescription: editData.appMetadata?.goodStation?.description || '',
        goodStationBannerUrl: editData.appMetadata?.goodStation?.bannerUrl || [],
        franchiseTitle: editData.contactMetadata?.franchise?.title || '',
        franchiseBannerUrl: editData.contactMetadata?.franchise?.bannerUrl || [],
        contactTitle: editData.contactMetadata?.contact?.title || 'Liên hệ chúng tôi',
        contactBannerUrl: editData.contactMetadata?.contact?.bannerUrl || []
      });

      if (editData.roomStyles && editData.roomStyles.length > 0) {
        setRoomStyles(editData.roomStyles);
      } else {
        setRoomStyles([{ title: '', bannerUrl: [], bannerMobileUrl: [] }]);
      }
    }
  }, [editData]);

  // Image validation function
  const validateImage = (file: File, maxSizeMB: number = 5, allowedTypes: string[] = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp']): string | null => {
    // Check file type
    if (!allowedTypes.includes(file.type)) {
      return `Chỉ chấp nhận file ảnh (JPG, PNG, WEBP)`;
    }
    
    // Check file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return `Kích thước file không được vượt quá ${maxSizeMB}MB`;
    }
    
    return null;
  };

  // Validate specific image dimensions
  const validateImageDimensions = (file: File, expectedWidth: number, expectedHeight: number): Promise<string | null> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        if (img.width !== expectedWidth || img.height !== expectedHeight) {
          resolve(`Kích thước ảnh phải là ${expectedWidth}x${expectedHeight}px (hiện tại: ${img.width}x${img.height}px)`);
        } else {
          resolve(null);
        }
      };
      img.onerror = () => resolve('Không thể đọc file ảnh');
      img.src = URL.createObjectURL(file);
    });
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileArrayChange = async (field: string, files: FileList | null, expectedWidth?: number, expectedHeight?: number) => {
    if (files) {
      const fileArray = Array.from(files);
      const newErrors = { ...errors };
      
      // Clear previous errors for this field
      delete newErrors[field];
      
      // Validate each file
      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];
        const fileKey = `${field}_${i}`;
        
        // Basic validation
        const basicError = validateImage(file);
        if (basicError) {
          newErrors[fileKey] = basicError;
          continue;
        }
        
        // Dimension validation if specified
        if (expectedWidth && expectedHeight) {
          try {
            const dimensionError = await validateImageDimensions(file, expectedWidth, expectedHeight);
            if (dimensionError) {
              newErrors[fileKey] = dimensionError;
            }
          } catch (error) {
            newErrors[fileKey] = 'Lỗi khi kiểm tra kích thước ảnh';
          }
        }
      }
      
      setErrors(newErrors);
      
      // Only update form data if no errors
      if (Object.keys(newErrors).length === 0) {
        setFormData(prev => ({
          ...prev,
          [field]: fileArray
        }));
      }
    }
  };

  // Function to render image preview
  const renderImagePreview = (images: File[] | string[], field: string, roomIndex?: number) => {
    if (!images || images.length === 0) return null;
    
    return (
      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
        {images.map((image, index) => (
          <div key={index} className="relative">
            <img
              src={typeof image === 'string' ? image : URL.createObjectURL(image)}
              alt={`Preview ${index + 1}`}
              className="w-full h-24 object-cover rounded border"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <div className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs cursor-pointer"
                 onClick={() => {
                   if (Array.isArray(images)) {
                     const newImages = images.filter((_, i) => i !== index);
                     if (roomIndex !== undefined) {
                       // Handle room styles
                       const updatedRoomStyles = [...roomStyles];
                       updatedRoomStyles[roomIndex] = { ...updatedRoomStyles[roomIndex], [field]: newImages };
                       setRoomStyles(updatedRoomStyles);
                     } else {
                       // Handle form data
                       setFormData(prev => ({
                         ...prev,
                         [field]: newImages
                       }));
                     }
                   }
                 }}>
              ×
            </div>
          </div>
        ))}
      </div>
    );
  };

  const addRoomStyle = () => {
    setRoomStyles([...roomStyles, { title: '', bannerUrl: [], bannerMobileUrl: [] }]);
  };

  const removeRoomStyle = (index: number) => {
    if (roomStyles.length > 1) {
      setRoomStyles(roomStyles.filter((_, i) => i !== index));
    }
  };

  const handleRoomStyleChange = (index: number, field: keyof RoomStyle, value: any) => {
    const updatedRoomStyles = [...roomStyles];
    updatedRoomStyles[index] = { ...updatedRoomStyles[index], [field]: value };
    setRoomStyles(updatedRoomStyles);
  };

  const handleRoomStyleFileChange = async (index: number, field: 'bannerUrl' | 'bannerMobileUrl', files: FileList | null) => {
    if (files) {
      const fileArray = Array.from(files);
      const newErrors = { ...errors };
      const fieldKey = `room_${index}_${field}`;
      
      // Clear previous errors for this field
      delete newErrors[fieldKey];
      
      // Validate each file
      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];
        const fileKey = `${fieldKey}_${i}`;
        
        // Basic validation
        const basicError = validateImage(file);
        if (basicError) {
          newErrors[fileKey] = basicError;
          continue;
        }
        
        // Dimension validation for room banners (709x936)
        try {
          const dimensionError = await validateImageDimensions(file, 709, 936);
          if (dimensionError) {
            newErrors[fileKey] = dimensionError;
          }
        } catch (error) {
          newErrors[fileKey] = 'Lỗi khi kiểm tra kích thước ảnh';
        }
      }
      
      setErrors(newErrors);
      
      // Only update room styles if no errors
      if (Object.keys(newErrors).length === 0) {
        const updatedRoomStyles = [...roomStyles];
        updatedRoomStyles[index] = { ...updatedRoomStyles[index], [field]: fileArray };
        setRoomStyles(updatedRoomStyles);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if there are any validation errors
    if (Object.keys(errors).length > 0) {
      alert('Vui lòng kiểm tra và sửa các lỗi validation trước khi submit');
      return;
    }
    
    setLoading(true);
    
    try {

      const submitData = {
        ...(isEditMode && editData?.id && { id: editData.id }),
        lang: formData.lang,
        isActive: formData.isActive,
        bannerUrl: formData.bannerUrl,
        bannerMobileUrl: formData.bannerMobileUrl,
        introMetadata: {
          branchMetadata: {
            bannerUrl: formData.introBranchBannerUrl
          },
          bannerUrl: formData.introBannerUrl,
          bannerMobileUrl: formData.introBannerMobileUrl
        },
        branchMetadata: {
          stats: {
            countBranch: formData.countBranch,
            countFrame: formData.countFrame,
            countUser: formData.countUser,
            countMember: formData.countMember
          },
          brand: {
            bannerMobileUrl: [],
            bannerUrl: formData.branchBrandBannerUrl,
            title: formData.branchBrandTitle,
            description: formData.branchBrandDescription
          }
        },
        roomStyles: roomStyles.map(room => ({
          bannerMobileUrl: [],
          bannerUrl: room.bannerUrl,
          title: room.title,
          _id: room._id
        })),
        appMetadata: {
          goodStation: {
            bannerMobileUrl: [],
            bannerUrl: formData.goodStationBannerUrl,
            title: formData.goodStationTitle,
            description: formData.goodStationDescription
          },
          bannerMobileUrl: [],
          bannerUrl: formData.appBannerUrl
        },
        contactMetadata: {
          franchise: {
            bannerUrl: formData.franchiseBannerUrl,
            bannerMobileUrl: [],
            title: formData.franchiseTitle
          },
          contact: {
            bannerUrl: formData.contactBannerUrl,
            bannerMobileUrl: [],
            title: formData.contactTitle
          }
        }
      };

      // console.log('Submitting data:', submitData);
      // console.log('Is edit mode:', isEditMode);
      
      const response = await request('landing-page-cms', submitData, isEditMode ? 'PUT' : 'POST');
      
      if (response?.status === true) {
        alert(isEditMode ? 'Cập nhật nội dung thành công!' : 'Tạo nội dung thành công!');
        navigate('/admin/content-landing-page-list');
      } else {
        alert(response?.message || 'Có lỗi xảy ra khi lưu dữ liệu');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Có lỗi xảy ra');
    } finally {
      setLoading(false);
    }
  };

  const classInput = "w-full border-[1px] border-[pink] rounded-md px-2 py-2 outline-none";
  const classLabel = "font-semibold text-[pink]";

  return (
    <div className="w-full">
      <div className="text-2xl text-center text-[pink] font-bold mb-6">
        {isEditMode ? 'CHỈNH SỬA NỘI DUNG' : 'TẠO MỚI NỘI DUNG'}
      </div>

      <form onSubmit={handleSubmit}>
        {/* Language Selection */}
        <div className="mb-6">
          <div className={classLabel}>Ngôn ngữ 1</div>
          <Select
            value={formData.lang}
            onChange={(value) => handleInputChange('lang', value)}
            size="large"
            className="custom-select w-[200px] text-[pink] border-[pink] rounded-md"
          >
            <Select.Option value="vi">Vietnamese</Select.Option>
            <Select.Option value="en">English</Select.Option>
            <Select.Option value="ko">Korean</Select.Option>
          </Select>
        </div>

        {/* TRANG CHỦ Section */}
        <div className="mb-8 p-4 bg-gray-50 rounded-lg">
          <h2 className="text-xl font-bold text-[pink] text-center mb-4">TRANG CHỦ</h2>
          
          <div className="mb-4">
            <div className={classLabel}>Ảnh banner trang chủ (Desktop)</div>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileArrayChange('bannerUrl', e.target.files, 1920, 1080)}
              className={`${classInput} ${errors.bannerUrl_0 ? 'border-red-500' : ''}`}
            />
            <div className="text-sm text-red-500 mt-1">Kích thước: 1920 x 1080</div>
            {errors.bannerUrl_0 && (
              <div className="text-sm text-red-500 mt-1">{errors.bannerUrl_0}</div>
            )}
          </div>

          <div className="mb-4">
            <div className={classLabel}>Ảnh banner trang chủ (Mobile)</div>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileArrayChange('bannerMobileUrl', e.target.files, 709, 936)}
              className={`${classInput} ${errors.bannerMobileUrl_0 ? 'border-red-500' : ''}`}
            />
            <div className="text-sm text-red-500 mt-1">Kích thước: 709 x 936</div>
            {renderImagePreview(formData.bannerMobileUrl, 'bannerMobileUrl')}
            {errors.bannerMobileUrl_0 && (
              <div className="text-sm text-red-500 mt-1">{errors.bannerMobileUrl_0}</div>
            )}
          </div>
        </div>

        {/* GIỚI THIỆU Section */}
        <div className="mb-8 p-4 bg-pink-100 rounded-lg">
          <h2 className="text-xl font-bold text-[pink] text-center mb-4">GIỚI THIỆU</h2>
          
          <div className="mb-4">
            <div className={classLabel}>Ảnh banner giới thiệu (Desktop)</div>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileArrayChange('introBannerUrl', e.target.files, 1920, 1080)}
              className={`${classInput} ${errors.introBannerUrl_0 ? 'border-red-500' : ''}`}
            />
            <div className="text-sm text-red-500 mt-1">Kích thước: 1920 x 1080</div>
            {renderImagePreview(formData.introBannerUrl, 'introBannerUrl')}
            {errors.introBannerUrl_0 && (
              <div className="text-sm text-red-500 mt-1">{errors.introBannerUrl_0}</div>
            )}
          </div>

          <div className="mb-4">
            <div className={classLabel}>Ảnh banner giới thiệu (Mobile)</div>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileArrayChange('introBannerMobileUrl', e.target.files, 709, 936)}
              className={`${classInput} ${errors.introBannerMobileUrl_0 ? 'border-red-500' : ''}`}
            />
            <div className="text-sm text-red-500 mt-1">Kích thước: 709 x 936</div>
            {renderImagePreview(formData.introBannerMobileUrl, 'introBannerMobileUrl')}
            {errors.introBannerMobileUrl_0 && (
              <div className="text-sm text-red-500 mt-1">{errors.introBannerMobileUrl_0}</div>
            )}
          </div>

          <div className="mb-4">
            <div className={classLabel}>Ảnh map chi nhánh</div>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileArrayChange('introBranchBannerUrl', e.target.files)}
              className={`${classInput} ${errors.introBranchBannerUrl_0 ? 'border-red-500' : ''}`}
            />
            {renderImagePreview(formData.introBranchBannerUrl, 'introBranchBannerUrl')}
            {errors.introBranchBannerUrl_0 && (
              <div className="text-sm text-red-500 mt-1">{errors.introBranchBannerUrl_0}</div>
            )}
          </div>
        </div>

        {/* Số liệu thống kê */}
        <div className="mb-8 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-bold text-[pink] mb-4">Số liệu thống kê</h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className={classLabel}>Tổng chi nhánh</div>
              <input
                type="number"
                value={formData.countBranch}
                onChange={(e) => handleInputChange('countBranch', parseInt(e.target.value) || 0)}
                className={classInput}
              />
            </div>
            
            <div>
              <div className={classLabel}>Tổng khung ảnh</div>
              <input
                type="number"
                value={formData.countFrame}
                onChange={(e) => handleInputChange('countFrame', parseInt(e.target.value) || 0)}
                className={classInput}
              />
            </div>
            
            <div>
              <div className={classLabel}>Tổng người dùng</div>
              <input
                type="number"
                value={formData.countUser}
                onChange={(e) => handleInputChange('countUser', parseInt(e.target.value) || 0)}
                className={classInput}
              />
            </div>
            
            <div>
              <div className={classLabel}>Thông thành viên</div>
              <input
                type="number"
                value={formData.countMember}
                onChange={(e) => handleInputChange('countMember', parseInt(e.target.value) || 0)}
                className={classInput}
              />
            </div>
          </div>

          <div className="mt-4">
            <div className={classLabel}>Tên thương hiệu</div>
            <input
              type="text"
              value={formData.branchBrandTitle}
              onChange={(e) => handleInputChange('branchBrandTitle', e.target.value)}
              className={classInput}
              placeholder="Tiêu đề thương hiệu"
            />
          </div>

          <div className="mt-4">
            <div className={classLabel}>Mô tả thương hiệu</div>
            <div style={{ height: '200px' }}>
              <ReactQuill
                theme="snow"
                value={formData.branchBrandDescription}
                onChange={(value) => handleInputChange('branchBrandDescription', value)}
                style={{ height: '150px' }}
              />
            </div>
          </div>

          <div className="mt-16">
            <div className={classLabel}>Ảnh giới thiệu thương hiệu</div>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileArrayChange('branchBrandBannerUrl', e.target.files, 200, 600)}
              className={`${classInput} ${errors.branchBrandBannerUrl_0 ? 'border-red-500' : ''}`}
            />
            <div className="text-sm text-red-500 mt-1">Kích thước: 200 x 600</div>
            {renderImagePreview(formData.branchBrandBannerUrl, 'branchBrandBannerUrl')}
            {errors.branchBrandBannerUrl_0 && (
              <div className="text-sm text-red-500 mt-1">{errors.branchBrandBannerUrl_0}</div>
            )}
          </div>
        </div>

        {/* PHÒNG Section */}
        <div className="mb-8 p-4 bg-pink-100 rounded-lg">
          <h2 className="text-xl font-bold text-[pink] text-center mb-4">PHÒNG</h2>
          
          {roomStyles.map((room, index) => (
            <div key={index} className="mb-6 p-4 border border-gray-200 rounded-lg bg-white">
              <div className="flex items-center justify-between mb-4">
                <div className="text-lg font-bold text-[pink]">
                  Phòng {index + 1}
                </div>
                {roomStyles.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRoomStyle(index)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Xóa
                  </button>
                )}
              </div>

              <div className="mb-4">
                <div className={classLabel}>Tên phòng</div>
                <input
                  type="text"
                  value={room.title}
                  onChange={(e) => handleRoomStyleChange(index, 'title', e.target.value)}
                  className={classInput}
                  placeholder={`Tiêu đề phòng ${index + 1}`}
                />
              </div>

              <div className="mb-4">
                <div className={classLabel}>Ảnh banner phòng</div>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => handleRoomStyleFileChange(index, 'bannerUrl', e.target.files)}
                  className={`${classInput} ${errors[`room_${index}_bannerUrl_0`] ? 'border-red-500' : ''}`}
                />
                <div className="text-sm text-red-500 mt-1">Kích thước: 709 x 936</div>
                {renderImagePreview(room.bannerUrl, 'bannerUrl', index)}
                {errors[`room_${index}_bannerUrl_0`] && (
                  <div className="text-sm text-red-500 mt-1">{errors[`room_${index}_bannerUrl_0`]}</div>
                )}
              </div>
            </div>
          ))}

          <div className="flex justify-center">
            <button
              type="button"
              onClick={addRoomStyle}
              className="flex items-center gap-2 px-6 py-3 bg-[pink] text-white rounded-md hover:bg-pink-600 transition-colors"
            >
              <span>Thêm phòng mới</span>
            </button>
          </div>
        </div>

        {/* ỨNG DỤNG Section */}
        <div className="mb-8 p-4 bg-pink-100 rounded-lg">
          <h2 className="text-xl font-bold text-[pink] text-center mb-4">ỨNG DỤNG</h2>
          
          <div className="mb-4">
            <div className={classLabel}>Ảnh banner (Desktop)</div>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileArrayChange('appBannerUrl', e.target.files, 710, 710)}
              className={`${classInput} ${errors.appBannerUrl_0 ? 'border-red-500' : ''}`}
            />
            <div className="text-sm text-red-500 mt-1">Kích thước: 710 x 710</div>
            {renderImagePreview(formData.appBannerUrl, 'appBannerUrl')}
            {errors.appBannerUrl_0 && (
              <div className="text-sm text-red-500 mt-1">{errors.appBannerUrl_0}</div>
            )}
          </div>

          <div className="mb-4">
            <div className={classLabel}>App - Good Station</div>
            <input
              type="text"
              onChange={(e) => handleInputChange('goodStationTitle', e.target.value)}
              className={classInput}
              placeholder="Tiêu đề"
            />
          </div>

          <div className="mb-4">
            <div style={{ height: '200px' }}>
              <ReactQuill
                theme="snow"
                value={formData.goodStationDescription}
                onChange={(value) => handleInputChange('goodStationDescription', value)}
                style={{ height: '150px' }}
              />
            </div>
          </div>

          <div className="mt-16">
            <div className={classLabel}>Ảnh sản phẩm banner (desktop)</div>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileArrayChange('goodStationBannerUrl', e.target.files, 710, 710)}
              className={`${classInput} ${errors.goodStationBannerUrl_0 ? 'border-red-500' : ''}`}
            />
            <div className="text-sm text-red-500 mt-1">Kích thước: 710 x 710</div>
            {renderImagePreview(formData.goodStationBannerUrl, 'goodStationBannerUrl')}
            {errors.goodStationBannerUrl_0 && (
              <div className="text-sm text-red-500 mt-1">{errors.goodStationBannerUrl_0}</div>
            )}
          </div>
        </div>

        {/* LIÊN HỆ - NHƯỢNG QUYỀN Section */}
        <div className="mb-8 p-4 bg-pink-100 rounded-lg">
          <h2 className="text-xl font-bold text-[pink] text-center mb-4">LIÊN HỆ - NHƯỢNG QUYỀN</h2>
          
          <div className="mb-6">
            <div className={classLabel}>Nhượng quyền</div>
            <input
              type="text"
              onChange={(e) => handleInputChange('franchiseTitle', e.target.value)}
              className={classInput}
              placeholder="Tiêu đề nhượng quyền"
            />
          </div>

          <div className="mb-6">
            <div className={classLabel}>Ảnh banner nhượng quyền</div>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileArrayChange('franchiseBannerUrl', e.target.files, 785, 1080)}
              className={`${classInput} ${errors.franchiseBannerUrl_0 ? 'border-red-500' : ''}`}
            />
            <div className="text-sm text-red-500 mt-1">Kích thước: 785 x 1080</div>
            {renderImagePreview(formData.franchiseBannerUrl, 'franchiseBannerUrl')}
            {errors.franchiseBannerUrl_0 && (
              <div className="text-sm text-red-500 mt-1">{errors.franchiseBannerUrl_0}</div>
            )}
          </div>

          <div className="mb-6">
            <div className={classLabel}>Liên hệ</div>
            <input
              type="text"
              value={formData.contactTitle}
              onChange={(e) => handleInputChange('contactTitle', e.target.value)}
              className={classInput}
              placeholder="Tiêu đề liên hệ"
            />
          </div>

          <div className="mb-6">
            <div className={classLabel}>Ảnh banner liên hệ</div>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileArrayChange('contactBannerUrl', e.target.files, 785, 1080)}
              className={`${classInput} ${errors.contactBannerUrl_0 ? 'border-red-500' : ''}`}
            />
            <div className="text-sm text-red-500 mt-1">Kích thước: 785 x 1080</div>
            {renderImagePreview(formData.contactBannerUrl, 'contactBannerUrl')}
            {errors.contactBannerUrl_0 && (
              <div className="text-sm text-red-500 mt-1">{errors.contactBannerUrl_0}</div>
            )}
          </div>
        </div>

        {/* Add Language Button */}
        {/* <div className="mb-6 flex justify-center">
          <button
            type="button"
            className="flex items-center gap-2 px-6 py-3 bg-[pink] text-white rounded-md hover:bg-pink-600 transition-colors"
          >
            <AiOutlinePlus className="h-5 w-5" />
            <span className="text-lg">Thêm ngôn ngữ khác</span>
          </button>
        </div> */}

        {/* Submit Button */}
        <div className="flex justify-center flex-col items-center">
          <button
            type="submit"
            disabled={loading}
            className="px-8 bg-[pink] text-white mb-20 py-2 rounded-md self-center hover:bg-pink-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (isEditMode ? 'Đang cập nhật...' : 'Đang tạo...') : (isEditMode ? 'Cập nhật' : 'Tạo mới')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateContentLandingPage;
